import React, { useState, useEffect } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { Award, MapPin, Calendar, Users, Globe, BookOpen, TrendingUp } from 'lucide-react';

const SuccessStoriesPage = () => {
  const arcaGreen = '#6B8E23';
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping
  const iconMap = {
    BookOpen: BookOpen,
    Users: Users,
    Globe: Globe,
    TrendingUp: TrendingUp,
    Award: Award
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/impact/success-stories`);
      const data = await response.json();

      if (data.success) {
        setStories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching success stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Members', value: '2,000+', icon: Users },
    { label: 'Countries', value: '16+', icon: Globe },
    { label: 'Training Programs', value: '35+', icon: BookOpen },
    { label: 'Major Events', value: '4+', icon: Award }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <div
        className="py-5 text-white"
        style={{
          background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)`,
          minHeight: '300px'
        }}
      >
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold mb-4">Success Stories</h1>
              <p className="lead mb-0">
                Inspiring stories of impact, growth, and transformation across Africa through ARCA's initiatives in 2025
              </p>
            </div>
            <div className="col-lg-4 text-center">
              <div
                className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{ width: '120px', height: '120px' }}
              >
                <Award style={{ color: arcaGreen }} size={60} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Statistics */}
      <div className="bg-light py-5">
        <div className="container">
          <h2 className="text-center mb-4" style={{ color: arcaGreen }}>Our 2025 Impact at a Glance</h2>
          <div className="row g-4">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="col-md-3 col-sm-6">
                  <div className="card border-0 shadow-sm h-100 text-center">
                    <div className="card-body p-4">
                      <div
                        className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '60px', height: '60px' }}
                      >
                        <Icon style={{ color: arcaGreen }} size={28} />
                      </div>
                      <h3 className="display-5 fw-bold mb-2" style={{ color: arcaGreen }}>{stat.value}</h3>
                      <p className="text-muted mb-0">{stat.label}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Success Stories Grid */}
      <div className="container py-5">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: arcaGreen }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No success stories found. Check back soon!</p>
          </div>
        ) : (
          <div className="row g-4">
            {stories.map((story, index) => {
              const Icon = iconMap[story.icon] || Award;
              return (
                <div key={index} className="col-lg-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div
                      className="card-header text-white py-3"
                      style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
                    >
                      <div className="d-flex align-items-center">
                        <Icon size={24} className="me-2" />
                        <span className="badge bg-white text-dark rounded-pill">{story.category}</span>
                      </div>
                    </div>
                    <div className="card-body p-4">
                      <h3 className="h4 mb-3" style={{ color: arcaGreen }}>{story.title}</h3>

                      <div className="mb-3">
                        <div className="d-flex align-items-center text-muted mb-2">
                          <MapPin size={16} className="me-2" />
                          <small>{story.location}</small>
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <Calendar size={16} className="me-2" />
                          <small>{story.date}</small>
                        </div>
                      </div>

                      <p className="text-dark mb-3">{story.description}</p>

                      <div className="bg-light p-3 rounded">
                        <p className="mb-0 fw-semibold" style={{ color: arcaGreen }}>
                          <Award size={18} className="me-2" />
                          Impact:
                        </p>
                        <p className="mb-0 text-muted">{story.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div
        className="py-5 text-white text-center"
        style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
      >
        <div className="container">
          <h2 className="mb-4">Be Part of Our Success Story</h2>
          <p className="lead mb-4">
            Join ARCA and contribute to advancing respiratory care across Africa
          </p>
          <a
            href="/membership_registration"
            className="btn btn-light btn-lg rounded-pill px-5"
            style={{ color: arcaGreen, fontWeight: '600' }}
          >
            Become a Member
          </a>
        </div>
      </div>
    </PublicLayout>
  );
};

export default SuccessStoriesPage;
